import {
  UserManager,
  User,
  WebStorageStateStore,
  type SigninSilentArgs,
  type UserManagerSettings,
} from 'oidc-client-ts';

import {
  ACCOUNT_SCOPES,
  AccountAuthorizationRequired,
  accountAuthorizationNeeded,
} from './accountAuthorization';
import { env } from '@/app/env';
import { isTerminalAuthError } from '@/lib/auth/session';

export const API_SCOPES =
  'openid profile email offline_access documents:read documents:write sections:read sections:write campaigns:read campaigns:write operations:read operations:write';

export const REFRESH_LOCK_NAME = 'knowsh:token-refresh';

// A transient refresh failure is shared by every caller that arrives inside
// this window, so a burst of queries can't turn one outage into a request storm.
export const FAILED_REFRESH_COOLDOWN_MS = 5_000;

/**
 * The identity shell rotates refresh tokens and revokes the whole family when
 * a rotated token is presented again. oidc-client-ts sends one token request
 * per signinSilent() call, so concurrent callers (queries racing at expiry,
 * automatic renew, a second tab sharing localStorage) replay the same token
 * and kill the session. Every refresh goes through one in-flight promise per
 * tab and one Web Lock across tabs; a caller that waited on another tab's
 * rotation takes the stored result instead of refreshing again.
 */
export class SingleFlightUserManager extends UserManager {
  private inFlight: Promise<User | null> | null = null;
  private failure: { error: unknown; at: number } | null = null;
  private restore: Promise<User | null> | null = null;
  private accountFlight: Promise<string> | null = null;
  private accountToken: {
    token: string;
    expiresAt: number;
    refreshToken: string;
    sub: string;
  } | null = null;

  getAccountAccessToken(): Promise<string> {
    this.accountFlight ??= this.accountOnce().finally(() => {
      this.accountFlight = null;
    });
    return this.accountFlight;
  }

  override async removeUser(): Promise<void> {
    this.accountToken = null;
    await super.removeUser();
  }

  async authorizeAccount(): Promise<void> {
    await this.inFlight;
    await this.accountFlight;
    const previous = await this.getUser();
    this.failure = null;
    const user = await this.signinSilent({
      forceIframeAuth: true,
      prompt: 'none',
      resource: [env.apiAudience, env.identity.accountResource],
      scope: `${API_SCOPES} ${ACCOUNT_SCOPES}`,
      extraTokenParams: { resource: env.apiAudience, scope: API_SCOPES },
    });
    this.accountToken = null;
    if (!user || user.profile.sub !== previous?.profile.sub)
      throw new AccountAuthorizationRequired();
  }

  private async accountOnce(): Promise<string> {
    await this.inFlight;
    const refresh = async () => {
      const user = await this.getUser();
      if (!user?.refresh_token) throw new AccountAuthorizationRequired();
      const cached = this.accountToken;
      if (
        cached &&
        cached.sub === user.profile.sub &&
        cached.refreshToken === user.refresh_token &&
        cached.expiresAt > Date.now() + 60_000
      )
        return cached.token;
      const endpoint = await this.metadataService.getTokenEndpoint();
      if (!endpoint) throw new Error('Connected apps token endpoint is unavailable. Retry.');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.settings.client_id,
          refresh_token: user.refresh_token,
          resource: env.identity.accountResource,
          scope: ACCOUNT_SCOPES,
        }),
      });
      const result = (await response.json()) as {
        access_token?: string;
        refresh_token?: string;
        expires_in?: number;
        error?: string;
      };
      if (!response.ok) {
        if (accountAuthorizationNeeded(result) || response.status === 401)
          throw new AccountAuthorizationRequired();
        throw new Error('Could not reach connected apps. Retry.');
      }
      if (!result.access_token) throw new Error('Connected apps did not return a token. Retry.');
      const current = await this.getUser();
      if (
        !current ||
        current.refresh_token !== user.refresh_token ||
        current.profile.sub !== user.profile.sub
      )
        throw new Error('Account changed. Retry.');
      const rotated = new User({
        ...current,
        refresh_token: result.refresh_token ?? current.refresh_token,
      });
      await this.storeUser(rotated);
      await this._events.load(rotated);
      this.accountToken = {
        token: result.access_token,
        expiresAt: Date.now() + (result.expires_in ?? 0) * 1000,
        refreshToken: rotated.refresh_token!,
        sub: rotated.profile.sub,
      };
      return result.access_token;
    };
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    return locks ? locks.request(REFRESH_LOCK_NAME, refresh) : refresh();
  }

  /** The end of the storage key the signed-in user is kept under, which every tab of this browser shares. */
  get storedUserKey(): string {
    return this._userStoreKey;
  }

  /**
   * A returning reader's stored session can hold an expired access token
   * next to a live refresh token. react-oidc-context reads the stored user
   * once at mount and calls an expired one signed out, and oidc-client-ts
   * schedules automatic renewal only for a token that has not expired yet,
   * so without this the title page asks a signed-in reader to sign in again.
   * One attempt per page load: a dead grant drops the stale user so the
   * signed-out page is honest; a transient failure keeps it for the next load.
   */
  restoreSession(): Promise<User | null> {
    this.restore ??= this.restoreOnce();
    return this.restore;
  }

  private async restoreOnce(): Promise<User | null> {
    const stored = await this.getUser();
    if (!stored?.expired || !stored.refresh_token) return stored;
    try {
      return await this.signinSilent();
    } catch (error) {
      if (isTerminalAuthError(error)) await this.removeUser();
      return null;
    }
  }

  override signinSilent(args?: SigninSilentArgs): Promise<User | null> {
    if (this.accountFlight)
      return this.accountFlight.catch(() => undefined).then(() => this.signinSilent(args));
    if (this.failure && Date.now() - this.failure.at < FAILED_REFRESH_COOLDOWN_MS) {
      return Promise.reject(this.failure.error);
    }
    this.inFlight ??= this.refreshOnce(args)
      .then((user) => {
        this.failure = null;
        return user;
      })
      .catch((error: unknown) => {
        this.failure = { error, at: Date.now() };
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
      });
    return this.inFlight;
  }

  private async refreshOnce(args?: SigninSilentArgs): Promise<User | null> {
    const before = (await this.getUser())?.refresh_token;
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks)
      return super.signinSilent({
        resource: env.apiAudience,
        extraTokenParams: { resource: env.apiAudience, scope: API_SCOPES },
        ...args,
      });
    return locks.request(REFRESH_LOCK_NAME, async () => {
      const current = await this.getUser();
      if (
        !args?.forceIframeAuth &&
        current?.refresh_token &&
        current.refresh_token !== before &&
        !current.expired
      ) {
        await this._events.load(current);
        return current;
      }
      return super.signinSilent({
        resource: env.apiAudience,
        extraTokenParams: { resource: env.apiAudience, scope: API_SCOPES },
        ...args,
      });
    });
  }
}

export function settings(): UserManagerSettings {
  const origin = window.location.origin;
  return {
    authority: env.identity.authority,
    client_id: env.identity.clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    scope: `${API_SCOPES} ${ACCOUNT_SCOPES}`,
    resource: [env.apiAudience, env.identity.accountResource],
    extraTokenParams: { resource: env.apiAudience, scope: API_SCOPES },
    refreshTokenAllowedScope: API_SCOPES,
    silent_redirect_uri: `${origin}/auth/callback`,
    response_type: 'code',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
}

let instance: SingleFlightUserManager | null = null;

export function getUserManager(): SingleFlightUserManager {
  instance ??= new SingleFlightUserManager(settings());
  return instance;
}
